/**
 * Controller do formulário "Seja um Influencer" (pedireito).
 *
 * Custom element `<pedireito-influencer-form>` que orquestra o fluxo em 3 passos:
 *   1. Enviar código de verificação para o e-mail.
 *   2. Validar o código → libera os demais campos.
 *   3. Enviar o cadastro completo para análise.
 *
 * Toda a comunicação com a inbazz passa pelo Worker (Bearer token server-side).
 * Configuração via data-attributes preenchidos pela seção Liquid:
 *   - data-proxy-url : URL do Cloudflare Worker
 *   - data-terms-url : URL dos termos de uso
 */

import { proxyRequest, lookupCep } from '@theme/pedireito-influencer-form-api';
import { maskCPF, maskCEP, maskDate, maskPhone, dateToISO, isValidCPF } from '@theme/pedireito-influencer-form-masks';

const UFS = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];

class PedireitoInfluencerForm extends HTMLElement {
  connectedCallback() {
    this.proxyUrl = this.dataset.proxyUrl || '';
    this.termsUrl = this.dataset.termsUrl || '';
    this.verifiedEmail = null;
    this.verificationToken = null;

    this.form = this.querySelector('.if-form');
    if (!this.form) return;

    this.populateStates();
    this.lockFields(true);
    this.bindEvents();
  }

  /** Atalho de query escopado ao componente. */
  $(selector) {
    return this.querySelector(selector);
  }

  /** Campos que ficam travados até o e-mail ser validado. */
  get lockableFields() {
    return this.querySelectorAll('.if-form [data-lockable]');
  }

  populateStates() {
    const select = this.$('#if-estado');
    UFS.forEach((uf) => {
      const option = document.createElement('option');
      option.value = uf;
      option.textContent = uf;
      select.appendChild(option);
    });
  }

  lockFields(locked) {
    this.lockableFields.forEach((field) => {
      field.disabled = locked;
    });
  }

  bindEvents() {
    this.$('#if-btn-send-code').addEventListener('click', () => this.handleSendCode());
    this.$('#if-btn-validate-code').addEventListener('click', () => this.handleValidateCode());
    this.$('#if-btn-change-email').addEventListener('click', () => this.reset());
    this.form.addEventListener('submit', (event) => this.handleSubmit(event));

    this.bindMask('#if-cpf', maskCPF);
    this.bindMask('#if-data-nascimento', maskDate);
    this.bindMask('#if-telefone', maskPhone);
    this.bindMask('#if-cep', maskCEP);

    this.$('#if-cpf').addEventListener('blur', (event) => this.validateCpfField(event.target));
    this.$('#if-cep').addEventListener('blur', (event) => this.handleCepLookup(event.target));
  }

  bindMask(selector, maskFn) {
    this.$(selector).addEventListener('input', (event) => {
      event.target.value = maskFn(event.target.value);
    });
  }

  // ---------------------------------------------------------------------------
  // Passo 1 — enviar código
  // ---------------------------------------------------------------------------
  async handleSendCode() {
    const emailInput = this.$('#if-email');
    const email = emailInput.value.trim();
    if (!email || !email.includes('@')) {
      alert('Por favor, insira um e-mail válido.');
      return;
    }

    const button = this.$('#if-btn-send-code');
    button.textContent = 'ENVIANDO...';
    button.disabled = true;

    try {
      await proxyRequest(this.proxyUrl, 'register/verify-email', { email });
      this.verifiedEmail = email;

      emailInput.disabled = true;
      button.hidden = true;
      this.$('#if-btn-change-email').hidden = false;
      this.$('#if-code-wrapper').hidden = false;

      alert(`Código de verificação enviado para ${email}. Verifique sua caixa de entrada.`);
    } catch (error) {
      alert(`Erro: ${error.message}`);
      button.textContent = 'ENVIAR CÓDIGO';
      button.disabled = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Passo 2 — validar código
  // ---------------------------------------------------------------------------
  async handleValidateCode() {
    const token = this.$('#if-codigo').value.trim();
    if (!token) {
      alert('Digite o código recebido por e-mail.');
      return;
    }

    const button = this.$('#if-btn-validate-code');
    button.textContent = 'VALIDANDO...';
    button.disabled = true;

    try {
      await proxyRequest(this.proxyUrl, 'register/validate-email', { email: this.verifiedEmail, token });
      this.verificationToken = token;

      this.$('#if-codigo').disabled = true;
      button.textContent = 'CÓDIGO VALIDADO ✓';
      button.classList.add('if-btn--validated');
      this.lockFields(false);
    } catch (error) {
      alert(`Código inválido: ${error.message}`);
      button.textContent = 'VALIDAR CÓDIGO';
      button.disabled = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Validações de campo
  // ---------------------------------------------------------------------------
  validateCpfField(input) {
    input.classList.remove('if-input--valid', 'if-input--invalid');
    if (!input.value.replace(/\D/g, '')) return;

    if (isValidCPF(input.value)) {
      input.classList.add('if-input--valid');
    } else {
      input.classList.add('if-input--invalid');
      alert('CPF inválido. Por favor, verifique o número digitado.');
    }
  }

  async handleCepLookup(input) {
    input.classList.remove('if-input--valid', 'if-input--invalid');
    if (input.value.replace(/\D/g, '').length !== 8) return;

    input.disabled = true;
    const address = await lookupCep(input.value);
    input.disabled = false;

    if (!address) {
      input.classList.add('if-input--invalid');
      alert('CEP não encontrado. Por favor, verifique o número digitado.');
      return;
    }

    input.classList.add('if-input--valid');
    if (address.uf) this.$('#if-estado').value = address.uf;
    if (address.localidade) this.$('#if-cidade').value = address.localidade;
    if (address.bairro) this.$('#if-bairro').value = address.bairro;
    if (address.logradouro) this.$('#if-logradouro').value = address.logradouro;
    this.$('#if-numero').focus();
  }

  // ---------------------------------------------------------------------------
  // Passo 3 — enviar cadastro
  // ---------------------------------------------------------------------------
  async handleSubmit(event) {
    event.preventDefault();

    if (!this.verificationToken) {
      alert('Você precisa validar seu e-mail primeiro.');
      return;
    }
    if (!isValidCPF(this.$('#if-cpf').value)) {
      alert('CPF inválido. Por favor, verifique o número digitado.');
      return;
    }
    if (!this.$('#if-cupom').value.trim()) {
      alert('Por favor, insira a sua sugestão de cupom.');
      return;
    }
    if (!this.$('#if-autorizacao').checked) {
      alert('Você precisa concordar com os termos de uso para continuar.');
      return;
    }

    const button = this.form.querySelector('.if-btn--submit');
    button.textContent = 'PROCESSANDO...';
    button.disabled = true;

    try {
      await proxyRequest(this.proxyUrl, 'register', this.buildPayload());
      alert('Cadastro enviado com sucesso para análise!');
      this.reset();
    } catch (error) {
      alert(`Erro ao enviar cadastro: ${error.message}`);
      button.textContent = 'ENVIAR PARA ANÁLISE';
      button.disabled = false;
    }
  }

  buildPayload() {
    const val = (selector) => this.$(selector).value.trim();
    const cupom = val('#if-cupom').toUpperCase();

    return {
      email: this.verifiedEmail,
      token: this.verificationToken,
      name: val('#if-nome'),
      last_name: val('#if-sobrenome'),
      doc: this.$('#if-cpf').value.replace(/\D/g, ''),
      birthday: dateToISO(val('#if-data-nascimento')),
      ddd: val('#if-ddd'),
      phone_number: this.$('#if-telefone').value.replace(/\D/g, ''),
      gender: val('#if-genero'),
      username: val('#if-instagram').replace('@', '').toLowerCase(),
      tiktok: val('#if-tiktok').replace('@', ''),
      cep: this.$('#if-cep').value.replace(/\D/g, ''),
      country: 'Brasil',
      address_state: val('#if-estado'),
      address_city: val('#if-cidade'),
      address_neighborhood: val('#if-bairro'),
      address_street: val('#if-logradouro'),
      address_number: val('#if-numero'),
      description_json: JSON.stringify({
        'Sugestão de Cupom': cupom
      }),
      prefixo: cupom,
      terms_file_url: this.termsUrl,
    };
  }

  // ---------------------------------------------------------------------------
  // Reset (alterar e-mail / pós-envio) — sem reload de página
  // ---------------------------------------------------------------------------
  reset() {
    this.verifiedEmail = null;
    this.verificationToken = null;
    this.form.reset();

    const emailInput = this.$('#if-email');
    emailInput.disabled = false;

    const sendButton = this.$('#if-btn-send-code');
    sendButton.hidden = false;
    sendButton.textContent = 'ENVIAR CÓDIGO';
    sendButton.disabled = false;

    this.$('#if-btn-change-email').hidden = true;
    this.$('#if-code-wrapper').hidden = true;

    const validateButton = this.$('#if-btn-validate-code');
    validateButton.textContent = 'VALIDAR CÓDIGO';
    validateButton.disabled = false;
    validateButton.classList.remove('if-btn--validated');
    this.$('#if-codigo').disabled = false;

    this.querySelectorAll('.if-input--valid, .if-input--invalid').forEach((el) => {
      el.classList.remove('if-input--valid', 'if-input--invalid');
    });

    this.lockFields(true);
  }
}

if (!customElements.get('pedireito-influencer-form')) {
  customElements.define('pedireito-influencer-form', PedireitoInfluencerForm);
}
